import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #controller = new AbortController();

  /** Whether the current pointer press started outside the card (on the dimmed backdrop). */
  #pressStartedOutside = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this.refs;

    // Capture so scrolling inside the dialog (the image list in the redesigned modal) is tracked too.
    dialog.addEventListener('scroll', this.handleScroll, { capture: true, signal });

    // The redesigned modal is a card over a dimmed page: clicking the dimmed area closes it, like the size guide.
    if (this.#isPdpModal) {
      dialog.addEventListener('pointerdown', (event) => (this.#pressStartedOutside = this.#isOutsideCard(event)), { signal });
      dialog.addEventListener('click', this.#handleBackdropClick, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Closes the redesigned modal when a click starts and ends on the backdrop, so dragging a zoomed
   * image past the card's edge doesn't close it.
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    const startedOutside = this.#pressStartedOutside;
    this.#pressStartedOutside = false;
    if (startedOutside && this.#isOutsideCard(event)) this.close();
  };

  /**
   * Backdrop clicks are dispatched to the <dialog> itself, at a point outside its box.
   * @param {MouseEvent} event
   */
  #isOutsideCard(event) {
    const { dialog } = this.refs;
    if (event.target !== dialog) return false;
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }

  /** Whether this is the redesigned PDP modal, which only opens on desktop. */
  get #isPdpModal() {
    return this.hasAttribute('data-pdp-modal');
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    // On phones the redesigned PDP keeps shoppers in the swipeable gallery instead of a full-screen viewer.
    if (this.#isPdpModal && isMobileBreakpoint()) return;

    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    // The redesigned card simply fades in (see the stylesheet); it doesn't grow out of the clicked image.
    if (this.#isPdpModal) {
      open();
      this.selectThumbnail(index, { behavior: 'instant' });
      return;
    }

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (this.#isPdpModal) return this.#fadeOutAndClose();
    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  /** Fades the redesigned card and its backdrop out, then closes it. */
  async #fadeOutAndClose() {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    if (prefersReducedMotion()) return this.closeDialog();

    dialog.classList.add('is-closing');
    await new Promise((resolve) => {
      const timeout = setTimeout(resolve, 500);
      dialog.addEventListener('transitionend', function onEnd(event) {
        if (event.target !== dialog || event.propertyName !== 'opacity') return;
        dialog.removeEventListener('transitionend', onEnd);
        clearTimeout(timeout);
        resolve(undefined);
      });
    });
    dialog.classList.remove('is-closing');
    this.closeDialog();
  }

  closeDialog() {
    const { dialog, media } = this.refs;
    dialog.close();
    for (const item of media) this.#setZoom(item, false);
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Toggles a 2x zoom on an image in the redesigned modal; the zoomed image follows the pointer.
   * @param {PointerEvent} event - The click event.
   */
  toggleZoom(event) {
    const item = event.target instanceof Element ? event.target.closest('li') : null;
    if (!(item instanceof HTMLElement)) return;

    const zoomed = !item.classList.contains('is-zoomed');
    this.#panZoom(item, event);
    this.#setZoom(item, zoomed);
  }

  /**
   * @param {HTMLElement} item - The media list item.
   * @param {boolean} zoomed - Whether the item should be zoomed.
   */
  #setZoom(item, zoomed) {
    item.classList.toggle('is-zoomed', zoomed);
    if (zoomed) {
      item.addEventListener('pointermove', this.#handleZoomPan);
    } else {
      item.removeEventListener('pointermove', this.#handleZoomPan);
    }
  }

  /** @param {PointerEvent} event */
  #handleZoomPan = (event) => {
    if (event.currentTarget instanceof HTMLElement) this.#panZoom(event.currentTarget, event);
  };

  /**
   * Points the zoom origin at the pointer position within the item.
   * @param {HTMLElement} item - The media list item.
   * @param {PointerEvent} event - The pointer event.
   */
  #panZoom(item, event) {
    const rect = item.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    item.style.setProperty('--zoom-origin-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
    item.style.setProperty('--zoom-origin-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
